import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi } from '../../api';

const TEST_CATEGORIES = ['Hematology', 'Biochemistry', 'Immunology', 'Microbiology', 'Pathology', 'Packages'] as const;

// ─── Inline SVG Icons ─────────────────────────────────────────────────────────

function IconFlask({ color, size = 24 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3h6" />
      <path d="M10 3v6l-4 9h12l-4-9V3" />
      <circle cx="9.5" cy="15.5" r="0.8" fill={color} stroke="none" />
      <circle cx="12.5" cy="17" r="0.6" fill={color} stroke="none" />
    </svg>
  );
}

function IconSearch({ color }: { color: string }) {
  return (
    <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function IconMapPin({ color }: { color: string }) {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconShield({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function IconHome({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function IconTestTube({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 2 20 7.5l-9 9-5.5-5.5z" />
      <path d="M5.5 11 3 13.5a2 2 0 0 0 0 2.8l4.7 4.7a2 2 0 0 0 2.8 0L13 18.5" />
    </svg>
  );
}

function IconPackage({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="21 8 21 21 3 21 3 8" />
      <rect x="1" y="3" width="22" height="5" />
      <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  );
}

function IconArrowRight({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
    </svg>
  );
}

function IconX({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    </svg>
  );
}

function IconFilter({ color }: { color: string }) {
  return (
    <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Lab {
  labProfileId?: string;
  id?: string;
  _id?: string;
  labName: string;
  address?: string;
  city?: string;
  accreditation?: string;
  verified?: boolean;
  homeCollectionAvailable?: boolean;
  testCount?: number;
  packageCount?: number;
}

// ─── Lab Card ─────────────────────────────────────────────────────────────────

function LabCard({ lab, onViewStore, onBookTest }: {
  lab: Lab;
  onViewStore: () => void;
  onBookTest: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const labId = lab.labProfileId || lab.id || lab._id || '';

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: T.white,
        border: `1.5px solid ${hovered ? T.teal + '50' : T.border}`,
        borderRadius: 18,
        padding: '16px 16px 14px',
        boxShadow: hovered ? T.shadow : '0 1px 4px rgba(0,0,0,0.06)',
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease, transform 0.12s ease',
        transform: hovered ? 'translateY(-1px)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {/* Top row: icon + name + badges */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {/* Icon bubble */}
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            background: T.teal + '14',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <IconFlask color={T.teal} size={22} />
        </div>

        {/* Name + badges */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span
              style={{
                fontFamily: T.font,
                fontSize: 14,
                fontWeight: 700,
                color: T.text,
                lineHeight: 1.3,
              }}
            >
              {lab.labName}
            </span>
            {(lab.accreditation || lab.verified) && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  background: '#059669' + '14',
                  border: `1px solid #05966930`,
                  borderRadius: 6,
                  padding: '1px 6px',
                  fontFamily: T.font,
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#059669',
                  letterSpacing: '0.04em',
                  whiteSpace: 'nowrap',
                }}
              >
                <IconShield color="#059669" />
                {/* Never invent an accreditation — a verified-but-unaccredited
                    lab is labelled Verified, not NABL. */}
                {lab.accreditation || 'Verified'}
              </span>
            )}
          </div>

          {/* City */}
          {lab.city && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                marginTop: 4,
              }}
            >
              <IconMapPin color={T.textTer} />
              <span
                style={{
                  fontFamily: T.font,
                  fontSize: 11,
                  color: T.textSec,
                }}
              >
                {lab.address ? `${lab.address}, ` : ''}{lab.city}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Chips row */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {lab.homeCollectionAvailable && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: T.teal + '12',
              border: `1px solid ${T.teal}28`,
              borderRadius: 8,
              padding: '3px 8px',
              fontFamily: T.font,
              fontSize: 10,
              fontWeight: 600,
              color: T.teal,
              whiteSpace: 'nowrap',
            }}
          >
            <IconHome color={T.teal} />
            Home collection
          </span>
        )}
        {lab.testCount != null && lab.testCount > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: T.navy + '0e',
              border: `1px solid ${T.navy}20`,
              borderRadius: 8,
              padding: '3px 8px',
              fontFamily: T.font,
              fontSize: 10,
              fontWeight: 600,
              color: T.navy,
              whiteSpace: 'nowrap',
            }}
          >
            <IconTestTube color={T.navy} />
            {lab.testCount} tests
          </span>
        )}
        {lab.packageCount != null && lab.packageCount > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: T.purple + '0e',
              border: `1px solid ${T.purple}28`,
              borderRadius: 8,
              padding: '3px 8px',
              fontFamily: T.font,
              fontSize: 10,
              fontWeight: 600,
              color: T.purple,
              whiteSpace: 'nowrap',
            }}
          >
            <IconPackage color={T.purple} />
            {lab.packageCount} packages
          </span>
        )}
      </div>

      {/* CTA buttons */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={onViewStore}
          style={{
            flex: 1,
            background: T.ice || '#F0F7FF',
            border: `1.5px solid ${T.border}`,
            borderRadius: 10,
            padding: '9px 0',
            fontFamily: T.font,
            fontSize: 12,
            fontWeight: 700,
            color: T.navy,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            transition: 'background 0.12s ease',
          }}
        >
          View Store
          <IconArrowRight color={T.navy} />
        </button>
        <button
          onClick={onBookTest}
          style={{
            flex: 1,
            background: `linear-gradient(135deg, ${T.teal} 0%, #00A8A8 100%)`,
            border: 'none',
            borderRadius: 10,
            padding: '9px 0',
            fontFamily: T.font,
            fontSize: 12,
            fontWeight: 700,
            color: T.white,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            boxShadow: `0 2px 8px ${T.teal}40`,
            transition: 'opacity 0.12s ease',
          }}
        >
          Book Test
          <IconArrowRight color={T.white} />
        </button>
      </div>
    </div>
  );
}

// ─── Skeleton Loader ──────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div
      style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: 18,
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ width: 46, height: 46, borderRadius: 14, background: T.border, flexShrink: 0, animation: 'pulse 1.4s ease infinite' }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ height: 14, borderRadius: 6, background: T.border, width: '65%' }} />
          <div style={{ height: 11, borderRadius: 6, background: T.border, width: '45%' }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <div style={{ height: 22, borderRadius: 8, background: T.border, width: 100 }} />
        <div style={{ height: 22, borderRadius: 8, background: T.border, width: 70 }} />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1, height: 36, borderRadius: 10, background: T.border }} />
        <div style={{ flex: 1, height: 36, borderRadius: 10, background: T.border }} />
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function LabDiscovery() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [homeCollection, setHomeCollection] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [labs, setLabs] = useState<Lab[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const fetchLabs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (search.trim()) params.search = search.trim();
      if (homeCollection) params.homeCollection = 'true';
      if (verifiedOnly) params.verified = 'true';
      const res = await patientApi.lab.searchLabs(params);
      const data = (res as any)?.data ?? res ?? [];
      // The lab directory is served by the shared provider API, which
      // returns { providers } of the generic marketplace shape — adapt it
      // to this page's Lab card fields (tags carry NABL/Home Collection).
      const raw: any[] = Array.isArray(data) ? data : data.providers ?? data.labs ?? [];
      let list: Lab[] = raw.map((p: any) => ({
        id: p.id ?? p._id,
        labName: p.labName ?? p.name ?? 'Lab',
        address: p.address ?? undefined,
        city: p.city ?? undefined,
        accreditation: p.accreditation ?? (p.tags?.includes('NABL') ? 'NABL' : undefined),
        verified: p.verified ?? p.isVerified ?? false,
        homeCollectionAvailable: p.homeCollectionAvailable ?? p.tags?.includes('Home Collection') ?? false,
        testCount: p.testCount ?? (Array.isArray(p.pricing) ? p.pricing.filter((x: any) => x.unit !== 'package').length : undefined),
        packageCount: p.packageCount ?? (Array.isArray(p.pricing) ? p.pricing.filter((x: any) => x.unit === 'package').length : undefined),
      }));
      if (homeCollection) {
        list = list.filter((l) => l.homeCollectionAvailable);
      }
      if (verifiedOnly) {
        list = list.filter((l) => l.verified || l.accreditation);
      }
      if (aliveRef.current) setLabs(list);
    } catch {
      if (aliveRef.current) { setLabs([]); setError('Could not load labs. Please try again.'); }
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [search, homeCollection, verifiedOnly]);

  useEffect(() => {
    fetchLabs();
  }, [fetchLabs]);

  // Debounced search: update search state 300ms after user stops typing
  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value.trim());
    }, 300);
  };

  const handleSearchSubmit = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearch(searchInput.trim());
  };

  const handleSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearchSubmit();
  };

  const clearSearch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchInput('');
    setSearch('');
  };

  const getLabId = (lab: Lab, index: number) => lab.labProfileId || lab.id || lab._id || `lab-${index}`;

  return (
    <div
      style={{
        background: T.bg,
        fontFamily: T.font,
        margin: '0 auto',
        paddingBottom: 100,
      }}
    >
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.45; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* ── Hero Header ─────────────────────────────────────────── */}
      <div
        style={{
          background: `linear-gradient(145deg, ${T.teal} 0%, #008B8B 55%, ${T.navy} 100%)`,
          padding: '44px 20px 64px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative blobs */}
        <div style={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <div style={{ position: 'absolute', bottom: 10, left: -20, width: 110, height: 110, borderRadius: '50%', background: 'rgba(0,40,80,0.15)' }} />

        {/* NHC badge */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 20,
            padding: '4px 12px',
            marginBottom: 16,
          }}
        >
          <IconFlask color="rgba(255,255,255,0.9)" size={12} />
          <span style={{ fontFamily: T.font, fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.08em' }}>
            DIAGNOSTIC LABS
          </span>
        </div>

        <h1 style={{ fontFamily: T.font, fontSize: 26, fontWeight: 800, color: T.white, margin: '0 0 8px', lineHeight: 1.2 }}>
          Find Labs
        </h1>
        <p style={{ fontFamily: T.font, fontSize: 13, color: 'rgba(255,255,255,0.78)', margin: 0, lineHeight: 1.6 }}>
          NABL-verified diagnostic labs near you
        </p>
      </div>

      {/* ── Search Bar (floating) ────────────────────────────────── */}
      <div style={{ padding: '0 16px', marginTop: -30, position: 'relative', zIndex: 10 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: T.white,
            borderRadius: 16,
            padding: '13px 16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            border: `1px solid ${T.border}`,
          }}
        >
          <div style={{ flexShrink: 0, opacity: 0.45 }}>
            <IconSearch color={T.navy} />
          </div>
          <input
            type="text"
            placeholder="Search by city, lab name, or test..."
            value={searchInput}
            onChange={(e) => handleSearchInputChange(e.target.value)}
            onKeyDown={handleSearchKey}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontFamily: T.font,
              fontSize: 13,
              color: T.text,
              background: 'transparent',
              padding: 0,
            }}
          />
          {searchInput.length > 0 && (
            <button
              onClick={clearSearch}
              style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', opacity: 0.5 }}
            >
              <IconX color={T.text} />
            </button>
          )}
          <button
            onClick={handleSearchSubmit}
            style={{
              flexShrink: 0,
              background: T.teal,
              border: 'none',
              borderRadius: 10,
              padding: '7px 14px',
              fontFamily: T.font,
              fontSize: 12,
              fontWeight: 700,
              color: T.white,
              cursor: 'pointer',
            }}
          >
            Search
          </button>
        </div>
      </div>

      {/* ── Filter Chips ─────────────────────────────────────────── */}
      <div style={{ padding: '14px 16px 0', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {/* Filter label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginRight: 2 }}>
          <IconFilter color={T.textTer} />
          <span style={{ fontFamily: T.font, fontSize: 11, color: T.textTer, fontWeight: 600 }}>Filter:</span>
        </div>

        {/* Home Collection toggle */}
        <button
          onClick={() => setHomeCollection(!homeCollection)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            background: homeCollection ? T.teal : T.white,
            border: `1.5px solid ${homeCollection ? T.teal : T.border}`,
            borderRadius: 20,
            padding: '6px 12px',
            fontFamily: T.font,
            fontSize: 12,
            fontWeight: 600,
            color: homeCollection ? T.white : T.textSec,
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            transition: 'background 0.12s ease, color 0.12s ease, border-color 0.12s ease',
          }}
        >
          <IconHome color={homeCollection ? T.white : T.textSec} />
          Home Collection
        </button>

        {/* Verified only toggle */}
        <button
          onClick={() => setVerifiedOnly(!verifiedOnly)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            background: verifiedOnly ? '#059669' : T.white,
            border: `1.5px solid ${verifiedOnly ? '#059669' : T.border}`,
            borderRadius: 20,
            padding: '6px 12px',
            fontFamily: T.font,
            fontSize: 12,
            fontWeight: 600,
            color: verifiedOnly ? T.white : T.textSec,
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            transition: 'background 0.12s ease, color 0.12s ease, border-color 0.12s ease',
          }}
        >
          <IconShield color={verifiedOnly ? T.white : T.textSec} />
          Verified / NABL
        </button>
      </div>

      {/* ── Category Chips ───────────────────────────────────────── */}
      <div style={{ padding: '10px 16px 0', display: 'flex', gap: 6, overflowX: 'auto', flexWrap: 'nowrap' }}>
        {TEST_CATEGORIES.map((cat) => {
          const active = selectedCategory === cat;
          return (
            <button
              key={cat}
              onClick={() => setSelectedCategory(active ? null : cat)}
              style={{
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                background: active ? T.navy : T.white,
                border: `1.5px solid ${active ? T.navy : T.border}`,
                borderRadius: 20,
                padding: '5px 12px',
                fontFamily: T.font,
                fontSize: 11,
                fontWeight: 600,
                color: active ? T.white : T.textSec,
                cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                transition: 'background 0.12s ease, color 0.12s ease, border-color 0.12s ease',
              }}
            >
              {cat}
            </button>
          );
        })}
      </div>

      {/* ── Results header ───────────────────────────────────────── */}
      <div style={{ padding: '18px 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ fontFamily: T.font, fontSize: 14, fontWeight: 800, color: T.text, margin: 0 }}>
          {loading ? 'Searching…' : `${labs.length} lab${labs.length !== 1 ? 's' : ''} found`}
        </h2>
        {(search || homeCollection || verifiedOnly || selectedCategory) && (
          <button
            onClick={() => { setSearchInput(''); setSearch(''); setHomeCollection(false); setVerifiedOnly(false); setSelectedCategory(null); }}
            style={{
              fontFamily: T.font,
              fontSize: 11,
              color: T.teal,
              fontWeight: 600,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* ── Lab Cards ────────────────────────────────────────────── */}
      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : error ? (
          <div
            style={{
              background: T.white,
              border: `1px solid #FECACA`,
              borderRadius: 16,
              padding: '28px 20px',
              textAlign: 'center',
            }}
          >
            <p style={{ fontFamily: T.font, fontSize: 13, color: '#DC2626', margin: '0 0 12px', fontWeight: 600 }}>
              {error}
            </p>
            <button
              onClick={fetchLabs}
              style={{
                background: T.teal,
                border: 'none',
                borderRadius: 10,
                padding: '9px 20px',
                fontFamily: T.font,
                fontSize: 13,
                fontWeight: 700,
                color: T.white,
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        ) : labs.length === 0 ? (
          <div
            style={{
              background: T.white,
              border: `1px solid ${T.border}`,
              borderRadius: 18,
              padding: '40px 20px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div style={{ width: 64, height: 64, borderRadius: 20, background: T.teal + '12', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconFlask color={T.teal} size={30} />
            </div>
            <p style={{ fontFamily: T.font, fontSize: 14, fontWeight: 700, color: T.text, margin: 0 }}>
              No labs found
            </p>
            <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: 0, lineHeight: 1.6 }}>
              Try adjusting your search or filters to find labs near you.
            </p>
            <button
              onClick={() => { setSearchInput(''); setSearch(''); setHomeCollection(false); setVerifiedOnly(false); setSelectedCategory(null); }}
              style={{
                background: T.teal,
                border: 'none',
                borderRadius: 10,
                padding: '9px 20px',
                fontFamily: T.font,
                fontSize: 12,
                fontWeight: 700,
                color: T.white,
                cursor: 'pointer',
                marginTop: 4,
              }}
            >
              Clear filters
            </button>
          </div>
        ) : (
          labs.map((lab, index) => {
            const id = getLabId(lab, index);
            return (
              <LabCard
                key={id}
                lab={lab}
                onViewStore={() => navigate(`/patient/care-services/labs/${lab.labProfileId || lab.id || lab._id || id}`)}
                onBookTest={() => navigate(`/patient/care-services/labs/${lab.labProfileId || lab.id || lab._id || id}/book`)}
              />
            );
          })
        )}
      </div>

      {/* NeoHealthCard branding */}
      <div style={{ padding: '20px 16px 8px', textAlign: 'center' }}>
        <span style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, lineHeight: 1.6 }}>
          NeoHealthCard™ · Verified diagnostic lab network · Every booking is consent-based and audit logged
        </span>
      </div>
    </div>
  );
}
